/** The primary layout expressed as a `TemplateLayoutV2` (its fields live flat on the scene). */
export function primaryLayout(scene) {
    return {
        orientation: scene.orientation,
        dimensions: scene.dimensions,
        scenes: scene.scenes,
    };
}
/** All layouts present on a scene, primary first. */
export function listTemplateLayouts(scene) {
    const layouts = [primaryLayout(scene)];
    if (scene.alternate) {
        layouts.push(scene.alternate);
    }
    return layouts;
}
/**
 * Picks the layout whose orientation matches the device format. Falls back to the primary layout
 * when there is no match (or no format reported). This is the single source of truth both
 * presenters use to choose which layout to render.
 */
export function selectTemplateLayout(scene, format) {
    if (!format) {
        return primaryLayout(scene);
    }
    return (listTemplateLayouts(scene).find((layout) => layout.orientation === format) ??
        primaryLayout(scene));
}
/** The opposite orientation. */
export function oppositeOrientation(orientation) {
    return orientation === "horizontal" ? "vertical" : "horizontal";
}
/** Swaps width and height — the natural target dimensions for the opposite orientation. */
export function transposeDimensions(dimensions) {
    return { width: dimensions.height, height: dimensions.width };
}
/**
 * Re-scales a layout's scenes from one set of dimensions to another, proportionally remapping each
 * layer's position and size. Used to seed a second-orientation layout from the first as an editable
 * starting point. Pure — returns new scene objects and does not mutate the input.
 *
 * Full-bleed media (`x:0, y:0, width:W, height:H`) naturally becomes `0,0,to.width,to.height`.
 */
export function rescaleScenes(scenes, from, to) {
    const sx = to.width / from.width;
    const sy = to.height / from.height;
    // Text height tracks font size; scale by the smaller axis so glyphs never overflow the box.
    const fontScale = Math.min(sx, sy);
    const cloned = JSON.parse(JSON.stringify(scenes));
    return cloned.map((scene) => ({
        ...scene,
        elements: scene.elements.map((element) => rescaleLayer(element, sx, sy, fontScale)),
    }));
}
function rescaleLayer(layer, sx, sy, fontScale) {
    const next = {
        ...layer,
        x: round(layer.x * sx),
        y: round(layer.y * sy),
    };
    if (typeof layer.width === "number") {
        next.width = round(layer.width * sx);
    }
    if (typeof layer.height === "number") {
        next.height = round(layer.height * sy);
    }
    if (typeof layer.offsetX === "number") {
        next.offsetX = round(layer.offsetX * sx);
    }
    if (typeof layer.offsetY === "number") {
        next.offsetY = round(layer.offsetY * sy);
    }
    if (layer.type === "text") {
        const text = layer;
        if (text.style && typeof text.style.fontSize === "number") {
            next.style = {
                ...text.style,
                fontSize: round(text.style.fontSize * fontScale),
            };
        }
    }
    return next;
}
function round(value) {
    return Math.round(value);
}
